import type { PeekUserResponse } from '@harmony/live-types'
import { useQuery } from '@tanstack/react-query'
import { env } from 'utils'

export const userPresenceQueryKey = (spaceId: string | undefined, userId: string | undefined) => [
    'userPresence',
    spaceId ?? 'noSpace',
    userId ?? 'noUser',
]

/**
 * Hook to check if a specific user is online in a space
 * Uses the peek HTTP endpoint for simple presence checks
 * Cached with TanStack Query (30s stale time)
 */
export function useUserPresenceQuery(
    spaceId: string | undefined,
    userId: string | undefined,
): { isOnline: boolean | undefined; isLoading: boolean } {
    const liveWorkerUrl = env.VITE_LIVE_WORKER_URL as string | undefined

    const { data, isLoading } = useQuery({
        queryKey: userPresenceQueryKey(spaceId, userId),
        queryFn: async () => {
            if (!liveWorkerUrl || !spaceId || !userId) {
                return undefined
            }
            const response = await fetch(`${liveWorkerUrl}/peek/${spaceId}/user/${userId}`)
            if (!response.ok) {
                return undefined
            }
            const data = (await response.json()) as PeekUserResponse
            // User is online if they are connected or live
            return data.liveState.type === 'connected' || data.liveState.type === 'live'
        },
        enabled: !!liveWorkerUrl && !!spaceId && !!userId,
        staleTime: 30_000, // Consider fresh for 30s
        gcTime: 60_000, // Keep in cache for 1 min
        retry: false, // Don't retry on failure
        meta: {
            // Suppress error logging
            errorMessage: false,
        },
    })

    return {
        isOnline: data,
        isLoading,
    }
}
